import React, { useState, useMemo } from 'react';

const GiftCardForm = ({
  createData,
  setCreateData,
  clientSearchTerm,
  setClientSearchTerm,
  filteredClients,
  handleSelectClientOption,
  handleClientKeyDown,
  showSuggestions,
  setShowSuggestions,
  activeSuggestionIndex,
  isLoading,
  handleCreateGiftCard,
  today,
  onFieldChange,
  availableServices
}) => {
  const [serviceFilter, setServiceFilter] = useState('');
  const [showServiceList, setShowServiceList] = useState(false);

  // Agrega un toggle manual para mostrar/ocultar la lista de servicios
  const toggleServiceList = () => {
    setShowServiceList((prev) => !prev);
  };

  // Manejar selección/deselección de servicios
  const handleServiceToggle = (service, checked) => {
    if (checked) {
      setCreateData(prevData => ({
        ...prevData,
        servicios: [
          ...prevData.servicios,
          {
            servicio_id: service.servicio_id,
            nombre: service.nombre,
            valor: service.precio
          }
        ]
      }));
    } else {
      setCreateData(prevData => ({
        ...prevData,
        servicios: prevData.servicios.filter(s => s.servicio_id !== service.servicio_id)
      }));
    }
  };

  // Calcular total de servicios
  const totalServicios = useMemo(
    () => createData.servicios.reduce((acc, s) => acc + Number(s.valor), 0),
    [createData.servicios]
  );

  // Filtrar servicios según el texto ingresado
  const filteredServices = useMemo(
    () =>
      availableServices.filter(service =>
        service.nombre.toLowerCase().includes(serviceFilter.toLowerCase())
      ),
    [availableServices, serviceFilter]
  );

  return (
    <form 
      onSubmit={handleCreateGiftCard} 
      className="max-w-2xl mx-auto bg-white shadow-lg rounded-xl p-8 space-y-6"
    >
      {/* Monto Total calculado */}
      <div className="bg-gray-100 rounded-lg p-4">
        <p className="text-lg font-semibold text-gray-800">
          Monto Total: ${totalServicios}
        </p>
      </div>

      {/* Fecha de Expiración */}
      <div>
        <label htmlFor="expires_at" className="block text-gray-800 font-semibold mb-1">
          Fecha de Expiración <span className="text-pink-600">*</span>
        </label>
        <input
          type="date"
          id="expires_at"
          value={createData.expires_at}
          onChange={(e) => {
            onFieldChange();
            setCreateData(prevData => ({ ...prevData, expires_at: e.target.value }));
          }}
          min={today}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 transition"
          required
        />
      </div>

      {/* Método de Pago */}
      <div>
        <label htmlFor="metodo_pago" className="block text-gray-800 font-semibold mb-1">
          Método de Pago
        </label>
        <select
          id="metodo_pago"
          value={createData.metodo_pago}
          onChange={(e) => {
            onFieldChange();
            setCreateData(prevData => ({ ...prevData, metodo_pago: e.target.value }));
          }}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 transition"
        >
          <option value="Efectivo">Efectivo</option>
          <option value="Transferencia">Transferencia</option>
          <option value="Mercado Pago">Mercado Pago</option>
        </select>
      </div>

      {/* Destinatario */}
      <div>
        <label htmlFor="destinatario_nombre" className="block text-gray-800 font-semibold mb-1">
          Destinatario <span className="text-pink-600">*</span>
        </label>
        <input
          type="text"
          id="destinatario_nombre"
          value={createData.destinatario_nombre}
          onChange={(e) => {
            onFieldChange();
            setCreateData(prevData => ({ ...prevData, destinatario_nombre: e.target.value }));
          }}
          placeholder="Nombre del destinatario"
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 transition"
          required
        />
      </div>

      {/* Servicios Seleccionables */}
      <div className="relative">
        <label className="block text-gray-800 font-semibold mb-1">
          Selecciona los Servicios <span className="text-pink-600">*</span>
        </label>

        {/* Botón para abrir/cerrar la lista de servicios */}
        <button
          type="button"
          onClick={toggleServiceList}
          className="mb-2 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white font-semibold rounded-lg shadow transition"
        >
          {showServiceList ? 'Ocultar Servicios' : 'Mostrar Servicios'}
        </button>

        {/* Campo para filtrar servicios */}
        {showServiceList && (
          <input
            type="text"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            placeholder="Buscar servicio..."
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 transition mb-2"
          />
        )}

        {showServiceList && (
          <ul
            className="bg-white border border-gray-300 rounded-lg max-h-64 overflow-y-auto z-10 shadow-lg p-2"
          >
            {filteredServices.length > 0 ? (
              filteredServices.map((service) => {
                const isChecked = createData.servicios.some(
                  (s) => s.servicio_id === service.servicio_id
                );
                return (
                  <li
                    key={service.servicio_id}
                    className={`px-4 py-2 cursor-pointer flex justify-between items-center ${
                      isChecked ? 'bg-pink-100' : 'hover:bg-gray-100'
                    }`}
                  >
                    <div onClick={() => handleServiceToggle(service, !isChecked)}>
                      <span className="font-medium text-gray-800">{service.nombre}</span>
                      <span className="ml-2 text-gray-500 text-sm">
                        (${service.precio})
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) =>
                        handleServiceToggle(service, e.target.checked)
                      }
                      onClick={(e) => e.stopPropagation()}
                      className="text-pink-600 focus:ring-pink-500 rounded"
                    />
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-2 text-gray-500 italic">No se encontraron servicios.</li>
            )}
          </ul>
        )}

        <div className="mt-3 text-right font-semibold text-gray-800">
          Total de servicios: <span className="text-pink-600">${totalServicios}</span>
        </div>
      </div>

      {/* Buscar Cliente (Comprador) */}
      <div className="relative">
        <label htmlFor="clientSearch" className="block text-gray-800 font-semibold mb-1">
          Buscar Cliente (comprador) <span className="text-pink-600">*</span>
        </label>
        <input
          type="text"
          id="clientSearch"
          value={clientSearchTerm}
          onChange={(e) => {
            onFieldChange();
            setClientSearchTerm(e.target.value);
            if (e.target.value === '') {
              setCreateData(prevData => ({ ...prevData, cliente_id: '' }));
            }
            setShowSuggestions(true);
          }}
          onKeyDown={handleClientKeyDown}
          onFocus={() => setShowSuggestions(true)}
          // Nota: si deseas que el dropdown no se cierre al hacer clic, quita el onBlur
          // onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder="Escribe nombre o apellido"
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 transition"
          required
        />
        {showSuggestions && filteredClients.length > 0 && (
          <ul className="absolute left-0 right-0 bg-white border border-gray-300 mt-1 rounded-lg max-h-60 overflow-y-auto z-10 shadow-lg">
            {filteredClients.map((client, index) => (
              <li
                key={client.cliente_id}
                onMouseDown={() => handleSelectClientOption(client)}
                className={`px-4 py-2 cursor-pointer ${
                  index === activeSuggestionIndex
                    ? 'bg-pink-100'
                    : 'hover:bg-gray-100'
                }`}
              >
                {client.nombre} {client.apellido}
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className={`w-full py-3 rounded-lg font-bold text-white transition ${
          isLoading ? 'bg-pink-400 cursor-not-allowed' : 'bg-pink-600 hover:bg-pink-700'
        }`}
      >
        {isLoading ? 'Creando...' : 'Crear Gift Card'}
      </button>
    </form>
  );
};

export default GiftCardForm;
